import PersonIcon from "@mui/icons-material/Person";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
	DataGridPremium,
	type GridCellParams,
	type GridColDef,
} from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { PaginationModel, SupplierMember } from "../types/UserTypes";

export type SupplierMembersDataGridProps = {
	rows: SupplierMember[];
	rowCount: number;
	isLoading: boolean;
	paginationModel: PaginationModel;
	onPaginationModelChange: (model: PaginationModel) => void;
	onImpersonateUser: (member: SupplierMember) => void;
};

export const SupplierMembersDataGrid = ({
	rows,
	rowCount,
	isLoading,
	paginationModel,
	onPaginationModelChange,
	onImpersonateUser,
}: SupplierMembersDataGridProps) => {
	const { t } = useTranslation();

	const columns: GridColDef<SupplierMember>[] = useMemo(
		() => [
			{
				field: "displayName",
				headerName: t("name"),
				flex: 1,
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				align: "right",
				headerAlign: "center",
				width: 100,
				renderCell: (params: GridCellParams<SupplierMember>) => (
					<Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", alignItems: "center", height: "100%" }}>
						<Tooltip title={t("impersonate-user")}>
							<IconButton
								onClick={() => onImpersonateUser(params.row)}
								data-testid="impersonate-button"
								size="small"
							>
								<PersonIcon />
							</IconButton>
						</Tooltip>
					</Box>
				),
			},
		],
		[t, onImpersonateUser],
	);

	return (
		<Box data-testid="supplier-members-data-grid">
			<DataGridPremium
				rows={rows}
				columns={columns}
				rowCount={rowCount}
				pagination
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={onPaginationModelChange}
				pageSizeOptions={[10, 25, 50]}
				loading={isLoading}
				disableRowSelectionOnClick
				localeText={{
					MuiTablePagination: {
						labelRowsPerPage: t("pagination.rows-per-page", { ns: "common" }),
					},
				} as never}
				sx={{
					border: "none",
					backgroundColor: "transparent",
				}}
			/>
		</Box>
	);
};
